import React from "react";
import { Link } from "react-router-dom";
import Loading from "ui/Loading";
import Grid from "ui/Grid";

import {
  useSchemaFunctionListQuery,
  SchemaFunctionListQuery,
} from "./gql/Query.generated";
import { useRoutes } from "utils/routes";
import LazyLoader from "ui/LazyLoader";
import { SortOrder } from "ui/Grid/util";
import { useQueryStringSort } from "utils/hooks";

type SchemaFunction = SchemaFunctionListQuery["getSchemaFunctions"][number];

type Props = {
  searchTerm: string | null;
  databaseId: string;
};

const NameAndIssuesCell: React.FunctionComponent<{
  databaseId: string;
  schemaFunction: SchemaFunction;
}> = ({ databaseId, schemaFunction }) => {
  const { databaseFunction } = useRoutes();
  return (
    <span>
      <Link to={databaseFunction(databaseId, schemaFunction.id)}>
        {schemaFunction.functionName}
      </Link>
      {schemaFunction.issues.map((i) => (
        <Link
          to={databaseFunction(databaseId, schemaFunction.id)}
          className={`state-${i.severity}`}
          title={`${i.references[0].name} ${i.description}`}
          key={i.id}
        >
          <i className="icon-exclamation-sign" />
        </Link>
      ))}
    </span>
  );
};

const FETCH_BATCH_SIZE = 50;

const Table: React.FunctionComponent<Props> = (props) => {
  const [sortOpts, setSortOpts] = useQueryStringSort<keyof SchemaFunction>({
    param: "sort",
    sortBy: "functionName",
    sortDirection: "DESC",
  });

  const { data, loading, error, fetchMore } = useSchemaFunctionListQuery({
    variables: {
      offset: 0,
      limit: FETCH_BATCH_SIZE,
      filter: props.searchTerm,
      sortBy: sortOpts.sortBy,
      sortDirection: sortOpts.sortDirection,
      databaseId: props.databaseId,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const schemaFunctions = data.getSchemaFunctions;

  const fetchMoreData = (loadedCount: number) => {
    return new Promise<boolean>((resolve) => {
      const offset = loadedCount;
      fetchMore({
        variables: { offset, limit: FETCH_BATCH_SIZE },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetched = fetchMoreResult.getSchemaFunctions;
          resolve(fetched.length > 0);
          if (!prev || !prev.getSchemaFunctions) {
            return fetchMoreResult;
          }
          return {
            ...prev,
            getSchemaFunctions: [...prev.getSchemaFunctions, ...fetched],
          };
        },
      });
    });
  };

  const handleSort = (sortBy: keyof SchemaFunction, sortOrder: SortOrder) => {
    setSortOpts({ sortBy, sortDirection: sortOrder });
  };

  return (
    <LazyLoader loadedCount={schemaFunctions.length} loadMore={fetchMoreData}>
      <Grid
        striped
        data={schemaFunctions}
        columns={[
          { field: "schemaName", header: "Schema", width: "120px" },
          {
            field: "functionName",
            header: "Function Name",
            width: "1fr",
            renderer: function SchemaFunctionNameCell({ rowData }) {
              return (
                <NameAndIssuesCell
                  databaseId={props.databaseId}
                  schemaFunction={rowData}
                />
              );
            },
          },
          { field: "language", header: "Language", width: "120px" },
          {
            field: "securityDefiner",
            header: "Security Definer",
            width: "160px",
            defaultSortOrder: "DESC",
            renderer: function SecurityDefinerCell({ fieldData }) {
              return fieldData ? "Yes" : "No";
            },
          },
        ]}
        sortedBy={sortOpts.sortBy}
        sortedOrder={sortOpts.sortDirection}
        handleSort={handleSort}
      />
    </LazyLoader>
  );
};

export default Table;
